import { Component, DestroyRef, effect, inject, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, switchMap } from 'rxjs';
import { Site } from 'src/app/models/centrex/site';
import { SiteService } from 'src/app/services/centrex/site.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-sites',
    templateUrl: './sites.component.html',
    styleUrls: ['./sites.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
    ]
})
export class SitesComponent {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['name', 'actions'];
    dataSource: MatTableDataSource<Site>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    filtersForm: UntypedFormGroup;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    constructor(
        private siteService: SiteService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.filtersForm = new UntypedFormGroup({});
        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    /**
     * Initialisation du formulaire des filtres du tableau des sites
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            id: [''],
            name: [''],
        });
    }

    loadDatas() {
        this.spinner.show();
        this.siteService
            .getSites()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (sites) => {
                    this.dataSource = new MatTableDataSource(sites);

                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;

                    this.createFilters();

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Site, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.id && data.id.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));
            matchFilter.push(data.name && data.name.trim().toLowerCase().includes(filterArray[1].trim().toLowerCase()));

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['name', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des sites
     */
    applyFilters() {
        const id = this.filtersForm.get('id').value;
        const name = this.filtersForm.get('name').value;

        const filterValue = (!!id ? id : '') + '¤' + (!!name ? name : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(siteId: string, siteName: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.SiteConfirmMessage', { siteName: siteName });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.siteService.deleteSite(siteId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(this.translate.instant('Sites.SuccessMessageDeleteSite', { siteName: siteName }));
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(
                        this.translate.instant('Sites.ErrorMessageDeleteSite', {
                            siteName: siteName,
                        })
                    );
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }
}
